// Remote cache for the game library. The /games endpoint is public but
// audience-aware: the catalog a caller sees depends on their access tier,
// so we key by user id (guests share one slot) to avoid mid-login flashes
// of the wrong catalog.

import { useMemo } from 'react';
import type { GameLibraryConfig } from '@airhead-games/shared/types';
import type { AccessTier } from '@airhead-games/shared/utilities';
import { getAllGames } from '../api/publicApi';
import { useAuth } from '../../hooks/useAuth';
import { createRemoteCache } from './createRemoteCache';

interface GameCachePayload {
  games: GameLibraryConfig[];
  accessTier: AccessTier;
}

export const gameCache = createRemoteCache<GameCachePayload>({
  name: 'games',
  initialData: { games: [], accessTier: 'standard' },
  fallbackError: 'Failed to load game library. Please try again.',
  fetcher: async () => {
    const response = await getAllGames();
    return {
      games: response.data.games,
      accessTier: response.data.accessTier,
    };
  },
});

interface UseGamesOptions {
  autoFetch?: boolean;
}

export interface UseGamesResult {
  games: GameLibraryConfig[];
  accessTier: AccessTier;
  loading: boolean;
  error: string | null;
  isFetched: boolean;
  refetch: () => Promise<void>;
}

export function useGames(options: UseGamesOptions = {}): UseGamesResult {
  const { user, isAuthInitialized } = useAuth();
  const audienceKey = user?._id ?? '__guest__';
  const cache = gameCache.useCache({
    autoFetch: options.autoFetch ?? isAuthInitialized,
    audienceKey,
  });

  return useMemo(
    () => ({
      games: cache.data.games,
      accessTier: cache.data.accessTier,
      loading: cache.loading,
      error: cache.error,
      isFetched: cache.isFetched,
      refetch: cache.refetch,
    }),
    [cache]
  );
}
